import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Button, Chip, Autocomplete,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Tabs, Tab, IconButton, InputAdornment, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import api from "../services/api";

const CATEGORIES = [
  "COPPER", "WIRE", "ALUMINUM", "BRASS", "LEAD", "STAINLESS",
  "MOTORS", "AUTO", "RADIATORS", "ZINC", "OTHER",
];

const guessCategory = (canonical) => {
  if (!canonical) return "";
  const c = canonical.toUpperCase();
  if (/^(COPPER|BARE BRIGHT|BERYLLIUM COPPER|CU )/.test(c)) return "COPPER";
  if (/^ICW |^CAT 5|^EXTENSION|^CHRISTMAS|^HARNESS|^URD|^HELIAX/.test(c)) return "WIRE";
  if (/^(ALUMINUM|AL |ACSR|NEOPRENE|CATV)/.test(c)) return "ALUMINUM";
  if (/^(BRASS|YELLOW|RED|HARD|SEMI|MAGNETIC|EDM|AD BRASS)/.test(c)) return "BRASS";
  if (/^LEAD/.test(c)) return "LEAD";
  if (/^(SS |STAINLESS)/.test(c)) return "STAINLESS";
  if (/^(MOTORS|ALTERNATORS|STARTERS|AUTO AC|SEALED UNITS|COMPRESSOR)/.test(c)) return "MOTORS";
  if (/^(AUTO RADIATOR|AUTO BATTERIES|AUTO SHREDDER|BATTERY|BALLAST)/.test(c)) return "AUTO";
  if (/RADIATOR/.test(c)) return "RADIATORS";
  if (/^DIE CAST/.test(c)) return "ZINC";
  return "OTHER";
};

export default function MappingManager() {
  const [tab, setTab] = useState(0);
  const [canonicals, setCanonicals] = useState([]);

  const refreshCanonicals = useCallback(() => {
    api.get("/priceMappings/canonicals")
      .then((r) => setCanonicals(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCanonicals([]));
  }, []);

  useEffect(() => { refreshCanonicals(); }, [refreshCanonicals]);

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Price Mapping Manager
      </Typography>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Unmapped Items" />
          <Tab label="All Mappings" />
        </Tabs>
      </Paper>

      {tab === 0 && <UnmappedTab canonicals={canonicals} onAfterSave={refreshCanonicals} />}
      {tab === 1 && <AllMappingsTab canonicals={canonicals} onAfterSave={refreshCanonicals} />}
    </Box>
  );
}

/* ================= Unmapped tab ================= */
function UnmappedTab({ canonicals, onAfterSave }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get("/priceMappings/unmapped")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("unmapped fetch failed:", e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.RawItem || "").toLowerCase().includes(q) ||
      (r.Vendor || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth autoFocus placeholder="Search raw item or vendor…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
        />
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
          {filtered.length} unmapped item{filtered.length === 1 ? "" : "s"} (only the latest date per vendor is shown).
          Mapping an item also backfills every historical price row with the same raw text.
        </Typography>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>Loading…</Paper>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          🎉 No unmapped items — every raw item has a canonical name.
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Raw Item (latest)</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell>Date</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={`${r.Vendor}::${r.RawItem}`} hover>
                  <TableCell><Chip size="small" label={r.Vendor} /></TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{r.RawItem}</TableCell>
                  <TableCell align="right">${Number(r.Price).toFixed(2)}</TableCell>
                  <TableCell>{r.PriceDate ? String(r.PriceDate).split("T")[0] : "-"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="contained" onClick={() => setEditing({
                      RawName: r.RawItem, CanonicalName: "", Category: "",
                    })}>
                      Map
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <MapDialog
        open={!!editing}
        initial={editing}
        canonicals={canonicals}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); onAfterSave(); }}
      />
    </>
  );
}

/* ================= All mappings tab ================= */
function AllMappingsTab({ canonicals, onAfterSave }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(() => {
    api.get("/priceMappings", { params: { search } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("mappings fetch failed:", e));
  }, [search]);

  useEffect(() => { refresh(); }, [refresh]);

  const del = async (id) => {
    if (!window.confirm("Delete this mapping? Associated price rows will be un-mapped.")) return;
    try {
      await api.delete(`/priceMappings/${id}`);
      refresh();
      onAfterSave();
    } catch (err) {
      alert("Delete failed: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          sx={{ flex: 1, minWidth: 240 }}
          placeholder="Search raw / canonical / category…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
        />
        <Button variant="contained" onClick={() => setEditing({
          RawName: "", CanonicalName: "", Category: "",
        })}>
          + New Mapping
        </Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Raw Name (what vendor's PDF says)</TableCell>
              <TableCell>Canonical (unified)</TableCell>
              <TableCell>Category</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.Mapping_ID} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{r.RawName}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{r.CanonicalName}</TableCell>
                <TableCell><Chip size="small" label={r.Category || "—"} /></TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => setEditing(r)}>
                    Edit
                  </Button>
                  <IconButton size="small" color="error" onClick={() => del(r.Mapping_ID)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <MapDialog
        open={!!editing}
        initial={editing}
        canonicals={canonicals}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); onAfterSave(); }}
      />
    </>
  );
}

/* ================= Edit / create dialog ================= */
function MapDialog({ open, initial, canonicals, onClose, onSaved }) {
  const [form, setForm] = useState({ RawName: "", CanonicalName: "", Category: "" });

  useEffect(() => {
    if (open && initial) setForm({
      RawName: initial.RawName || "",
      CanonicalName: initial.CanonicalName || "",
      Category: initial.Category || "",
    });
  }, [open, initial]);

  const onCanonicalChange = (val) => {
    setForm((prev) => ({
      ...prev,
      CanonicalName: val || "",
      Category: prev.Category || guessCategory(val),
    }));
  };

  const save = async () => {
    if (!form.RawName?.trim() || !form.CanonicalName?.trim()) {
      alert("Raw Name and Canonical Name are required.");
      return;
    }
    try {
      const r = await api.post("/priceMappings", form);
      alert(r.data?.message || "Saved");
      onSaved();
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.error || err.message));
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initial?.Mapping_ID ? "Edit Mapping" : "Map Raw Item to Canonical"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Raw Name (exact text from the vendor's PDF)"
            fullWidth required
            value={form.RawName}
            onChange={(e) => setForm({ ...form, RawName: e.target.value })}
            disabled={!!initial?.Mapping_ID}      // raw name is the key — don't let it change
            helperText={initial?.Mapping_ID ? "Raw name is the key; create a new mapping to change it." : "Match exactly — case-insensitive, leading/trailing spaces are trimmed."}
          />
          <Autocomplete
            freeSolo
            options={canonicals.map((c) => c.CanonicalName).filter(Boolean)}
            value={form.CanonicalName}
            onInputChange={(_, v) => onCanonicalChange(v)}
            onChange={(_, v) => onCanonicalChange(v)}
            renderInput={(params) => (
              <TextField {...params} label="Canonical Name (unified name across vendors)" required fullWidth
                helperText="Pick an existing one to group with other vendors, or type a new one."
              />
            )}
          />
          <TextField
            select label="Category" fullWidth
            value={form.Category || ""}
            onChange={(e) => setForm({ ...form, Category: e.target.value })}
          >
            {CATEGORIES.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>Save + Backfill</Button>
      </DialogActions>
    </Dialog>
  );
}
